import { useEffect, useMemo, useState } from 'react'
import { MiniBarChart } from '../components/charts/MiniBarChart'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody, CardHeader } from '../components/ui/Card'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { Dialog } from '../components/ui/Dialog'
import { EmptyState } from '../components/ui/EmptyState'
import { Field, Input, Select, Textarea } from '../components/ui/Field'
import { PageLoader } from '../components/ui/Spinner'
import {
    useAdminEmailLogs,
    useAdminPageviews,
    useAdminSettings,
    useAdminStats,
    useAdminUsers,
    useSendFreeMail,
    useSendTemplateMail,
    useSendTestMail,
    useSetSignupEnabled,
    type EmailLogStatusFilter,
    type EmailLogTypeFilter,
} from '../hooks/useAdminApi'
import {
    useAdminFeedback,
    useSetFeedbackStatus,
} from '../hooks/useFeedbackApi'
import { useT } from '../i18n/context'
import {
    EMAIL_LOG_TYPES,
    MAIL_TEMPLATE_KEYS,
    emailLogStatusMeta,
    emailLogTypeMeta,
    feedbackCategoryMeta,
    feedbackStatusMeta,
    invoiceStatusMeta,
} from '../lib/labelMaps'
import { errText } from '../lib/errors'
import {
    formatDate,
    formatDateTime,
    formatDurationCompact,
    formatMoney,
} from '../lib/format'
import { useAuthStore } from '../stores/auth'
import { toast } from '../stores/toast'
import type {
    AdminEmailLog,
    AdminUser,
    Feedback,
    FeedbackStatus,
    MailTemplateKey,
} from '../types/api'

export function AdminPage() {
    const t = useT()
    const stats = useAdminStats()

    return (
        <div className='space-y-4'>
            <div>
                <h1 className='text-xl font-bold text-slate-800 dark:text-slate-100'>
                    {t('admin.title')}
                </h1>
                <p className='mt-0.5 text-sm text-slate-500 dark:text-slate-400'>
                    {t('admin.subtitle')}
                </p>
            </div>

            {stats.isLoading ? (
                <Card>
                    <PageLoader />
                </Card>
            ) : stats.isError ? (
                <Card>
                    <EmptyState
                        title={t('admin.unavailable')}
                        description={t('admin.unavailableDesc')}
                    />
                </Card>
            ) : (
                stats.data && (
                    <>
                        <div className='grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6'>
                            <Kpi label={t('admin.kpiUsers')} value={String(stats.data.users.total)} />
                            <Kpi
                                label={t('admin.kpiVerified')}
                                value={String(stats.data.users.verified)}
                            />
                            <Kpi
                                label={t('admin.kpiUnverified')}
                                value={String(stats.data.users.unverified)}
                            />
                            <Kpi
                                label={t('admin.kpiWorkspaces')}
                                value={String(stats.data.workspaces.total)}
                            />
                            <Kpi
                                label={t('admin.kpiActive30')}
                                value={String(stats.data.activity.activeUsers30d)}
                            />
                            <Kpi
                                label={t('admin.kpiTracked30')}
                                value={formatDurationCompact(
                                    stats.data.activity.trackedSeconds30d,
                                )}
                            />
                        </div>

                        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                            <Card>
                                <CardHeader title={t('admin.registrationsTitle')} />
                                <CardBody>
                                    <MiniBarChart
                                        data={stats.data.users.registrationsPerDay}
                                        valueLabel={t('admin.registrationsLabel')}
                                    />
                                </CardBody>
                            </Card>
                            <Card>
                                <CardHeader title={t('admin.activeTitle')} />
                                <CardBody>
                                    <MiniBarChart
                                        data={stats.data.activity.activeUsersPerDay}
                                        valueLabel={t('admin.activeLabel')}
                                    />
                                </CardBody>
                            </Card>
                        </div>

                        <InvoiceStatusPanel
                            invoicesByStatus={stats.data.invoicesByStatus}
                        />
                    </>
                )
            )}

            <AdminSettingsPanel />
            <MailSendPanel />
            <EmailLogPanel />
            <FeedbackPanel />
            <PageviewsPanel />
            <UsersPanel />
        </div>
    )
}

function Kpi({ label, value }: { label: string; value: string }) {
    return (
        <div className='rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm'>
            <p className='text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                {label}
            </p>
            <p className='mt-1 text-2xl font-bold text-slate-800 dark:text-slate-100 tabular-nums'>
                {value}
            </p>
        </div>
    )
}

/** Statusverteilung als Badge-Zeilen + horizontale Balkenliste. */
function InvoiceStatusPanel({
    invoicesByStatus,
}: {
    invoicesByStatus: { status: string; count: number; totalSum: number }[]
}) {
    const t = useT()
    const max = Math.max(1, ...invoicesByStatus.map(s => s.count))
    return (
        <Card>
            <CardHeader title={t('admin.invoicesByStatus')} />
            <CardBody className='space-y-2.5'>
                {invoicesByStatus.length === 0 && (
                    <p className='text-sm text-slate-500 dark:text-slate-400'>
                        {t('admin.noInvoices')}
                    </p>
                )}
                {invoicesByStatus.map(s => {
                    const meta =
                        invoiceStatusMeta[
                            s.status as keyof typeof invoiceStatusMeta
                        ]
                    return (
                        <div key={s.status} className='flex items-center gap-3'>
                            <span className='w-24 shrink-0'>
                                {meta ? (
                                    <Badge tone={meta.tone}>
                                        {t(meta.labelKey)}
                                    </Badge>
                                ) : (
                                    s.status
                                )}
                            </span>
                            <div className='h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800'>
                                <div
                                    className='h-full rounded-full bg-indigo-600/75'
                                    style={{
                                        width: `${(s.count / max) * 100}%`,
                                    }}
                                />
                            </div>
                            <span className='w-10 text-right text-sm font-semibold text-slate-700 dark:text-slate-200 tabular-nums'>
                                {s.count}
                            </span>
                            <span className='w-24 text-right text-sm text-slate-500 dark:text-slate-400 tabular-nums'>
                                {formatMoney(s.totalSum)}
                            </span>
                        </div>
                    )
                })}
            </CardBody>
        </Card>
    )
}

/**
 * Betreiber-Einstellungen: Signup-Kill-Switch zur Laufzeit (DB-Wert,
 * überschreibt den ENV-Default dauerhaft) und Test-Mail zum Prüfen des
 * SMTP-Versandwegs. Empfänger ist defaultmäßig die eigene Admin-Adresse.
 */
function AdminSettingsPanel() {
    const t = useT()
    const settings = useAdminSettings()
    const setSignup = useSetSignupEnabled()
    const sendTestMail = useSendTestMail()
    const [signupIntent, setSignupIntent] = useState<boolean | null>(null)
    // Default-Empfänger ist die eigene Admin-Adresse (User steht wegen
    // RequireAdmin beim Mount bereits fest) — lazy statt Effect im Render.
    const [mailTo, setMailTo] = useState(
        () => useAuthStore.getState().user?.email ?? '',
    )

    const enabled = settings.data?.signupEnabled
    const mailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailTo)

    const confirmSignup = () => {
        if (signupIntent === null) return
        const activating = signupIntent
        setSignup.mutate(signupIntent, {
            onSuccess: () => {
                toast.success(
                    t(activating ? 'admin.signupActivated' : 'admin.signupDeactivated'),
                )
                setSignupIntent(null)
            },
            onError: err => toast.error(errText(err)),
        })
    }

    return (
        <Card>
            <CardHeader title={t('admin.settingsTitle')} />
            {settings.isLoading ? (
                <PageLoader />
            ) : settings.isError ? (
                <CardBody>
                    <EmptyState title={t('admin.settingsUnavailable')} />
                </CardBody>
            ) : (
                <CardBody className='space-y-4'>
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                        <div>
                            <div className='flex items-center gap-2'>
                                <span className='text-sm font-medium text-slate-800 dark:text-slate-100'>
                                    {t('admin.signupRow')}
                                </span>
                                {enabled !== undefined && (
                                    <Badge tone={enabled ? 'green' : 'red'}>
                                        {t(
                                            enabled
                                                ? 'admin.stateEnabled'
                                                : 'admin.stateDisabled',
                                        )}
                                    </Badge>
                                )}
                            </div>
                            <p className='mt-0.5 max-w-lg text-xs text-slate-500 dark:text-slate-400'>
                                {t('admin.signupDesc')}
                            </p>
                        </div>
                        {enabled !== undefined && (
                            <Button
                                variant={enabled ? 'danger' : 'primary'}
                                size='sm'
                                onClick={() => setSignupIntent(!enabled)}
                            >
                                {t(
                                    enabled
                                        ? 'admin.deactivateSignup'
                                        : 'admin.activateSignup',
                                )}
                            </Button>
                        )}
                    </div>

                    <div className='flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 dark:border-slate-700 pt-4'>
                        <div className='min-w-60 flex-1 max-w-md'>
                            <p className='text-sm font-medium text-slate-800 dark:text-slate-100'>
                                {t('admin.testMailRow')}
                            </p>
                            <p className='mt-0.5 text-xs text-slate-500 dark:text-slate-400'>
                                {t('admin.testMailDesc')}
                            </p>
                            <Input
                                type='email'
                                value={mailTo}
                                onChange={e => setMailTo(e.target.value)}
                                placeholder={t('admin.testMailTo')}
                                className='mt-2'
                            />
                        </div>
                        <Button
                            variant='secondary'
                            size='sm'
                            disabled={!mailValid}
                            loading={sendTestMail.isPending}
                            onClick={() =>
                                sendTestMail.mutate(mailTo, {
                                    onSuccess: () =>
                                        toast.success(t('admin.testMailSent')),
                                    onError: err => toast.error(errText(err)),
                                })
                            }
                        >
                            {t('admin.sendTestMail')}
                        </Button>
                    </div>
                </CardBody>
            )}
            <ConfirmDialog
                open={signupIntent !== null}
                message={t(
                    signupIntent
                        ? 'admin.confirmActivate'
                        : 'admin.confirmDeactivate',
                )}
                confirmLabel={t(
                    signupIntent
                        ? 'admin.activateSignup'
                        : 'admin.deactivateSignup',
                )}
                tone={signupIntent ? 'primary' : 'danger'}
                loading={setSignup.isPending}
                onConfirm={confirmSignup}
                onClose={() => setSignupIntent(null)}
            />
        </Card>
    )
}

/**
 * E-Mail-Log: jede über den zentralen Mail-Dispatch gelaufene Mail
 * (Erfolg UND Fehler) — Suche nach Empfänger/Betreff, Status- und
 * Typ-Filter, „Mehr laden" wie UsersPanel.
 */
/**
 * E-Mail-Versand für Betreiber: jede Mail-Vorlage als Preview in de/en
 * (Beispiel-Context statt echter Vorgänge) oder eine Mail mit freiem
 * Text und freiem Betreff im Marken-Layout. Landet beides im E-Mail-Log.
 */
function MailSendPanel() {
    const t = useT()
    const sendTemplateMail = useSendTemplateMail()
    const sendFreeMail = useSendFreeMail()
    const [mode, setMode] = useState<'template' | 'free'>('template')
    const [template, setTemplate] = useState<MailTemplateKey>(
        'emailVerification',
    )
    const [language, setLanguage] = useState<'de' | 'en'>('de')
    // Default-Empfänger wie beim Test-Mail-Row: die eigene Admin-Adresse
    const [mailTo, setMailTo] = useState(
        () => useAuthStore.getState().user?.email ?? '',
    )
    const [subject, setSubject] = useState('')
    const [text, setText] = useState('')

    const mailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailTo)
    const freeValid = subject.trim().length > 0 && text.trim().length > 0
    // Nur diese beiden Vorlagen hätten produktiv einen PDF-Anhang
    const attachmentTemplate =
        template === 'invoiceMail' || template === 'dunningMail'

    return (
        <Card>
            <CardHeader
                title={t('admin.sendMailTitle')}
                subtitle={t('admin.sendMailDesc')}
            />
            <CardBody className='space-y-4'>
                <div
                    className='flex w-fit items-center rounded-lg border border-slate-300 dark:border-slate-600 p-0.5'
                    role='group'
                    aria-label={t('admin.sendMailTitle')}
                >
                    {(['template', 'free'] as const).map(value => (
                        <button
                            key={value}
                            type='button'
                            onClick={() => setMode(value)}
                            aria-pressed={mode === value}
                            className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                                mode === value
                                    ? 'bg-indigo-700 dark:bg-indigo-600 text-white'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
                            }`}
                        >
                            {t(
                                value === 'template'
                                    ? 'admin.sendMailModeTemplate'
                                    : 'admin.sendMailModeFree',
                            )}
                        </button>
                    ))}
                </div>

                {mode === 'template' ? (
                    <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                        <Field label={t('admin.sendMailTemplate')}>
                            <Select
                                value={template}
                                onChange={e =>
                                    setTemplate(
                                        e.target.value as MailTemplateKey,
                                    )
                                }
                            >
                                {MAIL_TEMPLATE_KEYS.map(key => (
                                    <option key={key} value={key}>
                                        {t(emailLogTypeMeta[key].labelKey)}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Field label={t('admin.sendMailLanguage')}>
                            <Select
                                value={language}
                                onChange={e =>
                                    setLanguage(
                                        e.target.value as 'de' | 'en',
                                    )
                                }
                            >
                                <option value='de'>
                                    {t('settings.languageDe')}
                                </option>
                                <option value='en'>
                                    {t('settings.languageEn')}
                                </option>
                            </Select>
                        </Field>
                        <Field label={t('admin.sendMailTo')}>
                            <Input
                                type='email'
                                value={mailTo}
                                onChange={e => setMailTo(e.target.value)}
                                placeholder={t('admin.testMailTo')}
                            />
                        </Field>
                    </div>
                ) : (
                    <div className='space-y-3'>
                        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                            <Field label={t('admin.sendMailTo')}>
                                <Input
                                    type='email'
                                    value={mailTo}
                                    onChange={e => setMailTo(e.target.value)}
                                    placeholder={t('admin.testMailTo')}
                                />
                            </Field>
                            <Field label={t('admin.sendMailSubject')}>
                                <Input
                                    value={subject}
                                    maxLength={200}
                                    onChange={e => setSubject(e.target.value)}
                                    placeholder={t('admin.sendMailSubject')}
                                />
                            </Field>
                        </div>
                        <Field label={t('admin.sendMailText')}>
                            <Textarea
                                value={text}
                                maxLength={5000}
                                rows={5}
                                onChange={e => setText(e.target.value)}
                                placeholder={t('admin.sendMailTextPlaceholder')}
                            />
                        </Field>
                    </div>
                )}

                <div className='flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 dark:border-slate-700 pt-4'>
                    <p className='text-xs text-slate-500 dark:text-slate-400'>
                        {mode === 'template' && attachmentTemplate
                            ? t('admin.sendMailNoAttachmentHint')
                            : ''}
                    </p>
                    {mode === 'template' ? (
                        <Button
                            variant='secondary'
                            size='sm'
                            disabled={!mailValid}
                            loading={sendTemplateMail.isPending}
                            onClick={() =>
                                sendTemplateMail.mutate(
                                    { to: mailTo, template, language },
                                    {
                                        onSuccess: () =>
                                            toast.success(
                                                t('admin.sendMailSent'),
                                            ),
                                        onError: err =>
                                            toast.error(errText(err)),
                                    },
                                )
                            }
                        >
                            {t('admin.sendMailButton')}
                        </Button>
                    ) : (
                        <Button
                            variant='secondary'
                            size='sm'
                            disabled={!mailValid || !freeValid}
                            loading={sendFreeMail.isPending}
                            onClick={() =>
                                sendFreeMail.mutate(
                                    { to: mailTo, subject, text },
                                    {
                                        onSuccess: () =>
                                            toast.success(
                                                t('admin.sendMailSent'),
                                            ),
                                        onError: err =>
                                            toast.error(errText(err)),
                                    },
                                )
                            }
                        >
                            {t('admin.sendMailButton')}
                        </Button>
                    )}
                </div>
            </CardBody>
        </Card>
    )
}

function EmailLogPanel() {
    const t = useT()
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<EmailLogStatusFilter>('ALL')
    const [typeFilter, setTypeFilter] = useState<EmailLogTypeFilter>('ALL')

    // Debounce für die serverseitige Suche
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 300)
        return () => clearTimeout(timer)
    }, [searchInput])

    const logs = useAdminEmailLogs(search, statusFilter, typeFilter)
    const rows = logs.data?.pages.flatMap(p => p.data) ?? []
    const meta = logs.data?.pages[logs.data.pages.length - 1]?.meta
    const hasMore = logs.hasNextPage

    return (
        <Card>
            <CardHeader
                title={t('admin.emailLogTitle')}
                actions={
                    <div className='flex flex-wrap items-center gap-2'>
                        <Input
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            placeholder={t('admin.emailLogSearch')}
                            className='w-56'
                        />
                        <Select
                            value={statusFilter}
                            onChange={e =>
                                setStatusFilter(
                                    e.target.value as EmailLogStatusFilter,
                                )
                            }
                            className='w-40'
                        >
                            <option value='ALL'>
                                {t('admin.emailFilterAll')}
                            </option>
                            <option value='SENT'>
                                {t('admin.emailStatusSent')}
                            </option>
                            <option value='FAILED'>
                                {t('admin.emailStatusFailed')}
                            </option>
                        </Select>
                        <Select
                            value={typeFilter}
                            onChange={e =>
                                setTypeFilter(e.target.value as EmailLogTypeFilter)
                            }
                            className='w-48'
                        >
                            <option value='ALL'>
                                {t('admin.emailTypeAll')}
                            </option>
                            {EMAIL_LOG_TYPES.map(type => (
                                <option key={type} value={type}>
                                    {t(emailLogTypeMeta[type].labelKey)}
                                </option>
                            ))}
                        </Select>
                    </div>
                }
            />
            {logs.isLoading ? (
                <PageLoader />
            ) : rows.length === 0 ? (
                <EmptyState
                    title={t('admin.noEmailLogs')}
                    description={t('admin.noEmailLogsDesc')}
                />
            ) : (
                <CardBody className='p-0'>
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                    <th className='px-4 py-2.5'>
                                        {t('admin.emailColTime')}
                                    </th>
                                    <th className='px-4 py-2.5'>
                                        {t('admin.emailColType')}
                                    </th>
                                    <th className='px-4 py-2.5'>
                                        {t('admin.emailColRecipient')}
                                    </th>
                                    <th className='px-4 py-2.5'>
                                        {t('admin.emailColSubject')}
                                    </th>
                                    <th className='px-4 py-2.5 text-right'>
                                        {t('admin.emailColStatus')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((log: AdminEmailLog) => {
                                    const type = emailLogTypeMeta[log.mailType]
                                    const status = emailLogStatusMeta[log.status]
                                    return (
                                        <tr
                                            key={log.id}
                                            className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                        >
                                            <td className='px-4 py-2.5 whitespace-nowrap text-slate-500 dark:text-slate-400'>
                                                {formatDateTime(log.createdAt)}
                                            </td>
                                            <td className='px-4 py-2.5'>
                                                <Badge tone={type.tone}>
                                                    {t(type.labelKey)}
                                                </Badge>
                                            </td>
                                            <td className='px-4 py-2.5'>
                                                <span className='block font-medium text-slate-800 dark:text-slate-100'>
                                                    {log.recipient}
                                                </span>
                                                {log.cc && (
                                                    <span className='block text-xs text-slate-500 dark:text-slate-400'>
                                                        CC: {log.cc}
                                                    </span>
                                                )}
                                            </td>
                                            <td className='max-w-sm px-4 py-2.5'>
                                                <span
                                                    className='block truncate text-slate-600 dark:text-slate-300'
                                                    title={log.subject}
                                                >
                                                    {log.subject}
                                                </span>
                                                {log.status === 'FAILED' &&
                                                    log.errorText && (
                                                        <span
                                                            className='block truncate text-xs text-red-600 dark:text-red-400'
                                                            title={log.errorText}
                                                        >
                                                            {log.errorText}
                                                        </span>
                                                    )}
                                            </td>
                                            <td className='px-4 py-2.5'>
                                                <div className='flex items-center justify-end gap-1.5'>
                                                    <Badge tone={status.tone}>
                                                        {t(status.labelKey)}
                                                    </Badge>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className='flex items-center justify-between border-t border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400'>
                        <span>
                            {t('admin.emailLogsOf', {
                                shown: rows.length,
                                total: meta?.total ?? rows.length,
                            })}
                        </span>
                        {hasMore && (
                            <Button
                                variant='secondary'
                                size='sm'
                                loading={logs.isFetchingNextPage}
                                onClick={() => void logs.fetchNextPage()}
                            >
                                {t('admin.loadMore')}
                            </Button>
                        )}
                    </div>
                </CardBody>
            )}
        </Card>
    )
}

function PageviewsPanel() {
    const t = useT()
    const pageviews = useAdminPageviews()

    const top = useMemo(() => {
        const byPath = new Map<string, number>()
        for (const row of pageviews.data ?? []) {
            byPath.set(row.path, (byPath.get(row.path) ?? 0) + row.count)
        }
        return [...byPath.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
    }, [pageviews.data])

    return (
        <Card>
            <CardHeader title={t('admin.pageviewsTitle')} />
            {pageviews.isLoading ? (
                <PageLoader />
            ) : top.length === 0 ? (
                <EmptyState title={t('admin.noPageviews')} />
            ) : (
                <CardBody className='p-0'>
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                <th className='px-4 py-2.5'>{t('admin.path')}</th>
                                <th className='px-4 py-2.5 text-right'>
                                    {t('admin.views')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {top.map(([path, count]) => (
                                <tr
                                    key={path}
                                    className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                >
                                    <td className='px-4 py-2.5 font-mono text-slate-700 dark:text-slate-200'>
                                        {path}
                                    </td>
                                    <td className='px-4 py-2.5 text-right font-medium text-slate-800 dark:text-slate-100 tabular-nums'>
                                        {count}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardBody>
            )}
        </Card>
    )
}

/**
 * Eingegangenes Nutzer-Feedback: Status-Filter + „Mehr laden" wie UsersPanel,
 * je Zeile Status-Übergänge OPEN → READ → DONE (und zurück zu OPEN).
 */
function FeedbackPanel() {
    const t = useT()
    const [statusFilter, setStatusFilter] = useState<FeedbackStatus | 'ALL'>(
        'OPEN',
    )
    const [detail, setDetail] = useState<Feedback | null>(null)
    const feedback = useAdminFeedback(statusFilter)
    const setStatus = useSetFeedbackStatus()

    const rows = feedback.data?.pages.flatMap(p => p.data) ?? []
    const meta = feedback.data?.pages[feedback.data.pages.length - 1]?.meta
    const hasMore = feedback.hasNextPage

    /** Zeilen-spezifischer Spin, damit nicht alle Buttons laden. */
    const rowPending = (id: number) =>
        setStatus.isPending && setStatus.variables?.id === id

    return (
        <Card>
            <CardHeader
                title={t('feedback.adminTitle')}
                actions={
                    <Select
                        value={statusFilter}
                        onChange={e =>
                            setStatusFilter(
                                e.target.value as FeedbackStatus | 'ALL',
                            )
                        }
                        className='w-44'
                    >
                        <option value='ALL'>{t('feedback.filterAll')}</option>
                        <option value='OPEN'>{t('feedback.statusOpen')}</option>
                        <option value='READ'>{t('feedback.statusRead')}</option>
                        <option value='DONE'>{t('feedback.statusDone')}</option>
                    </Select>
                }
            />
            {feedback.isLoading ? (
                <PageLoader />
            ) : rows.length === 0 ? (
                <EmptyState
                    title={t('feedback.noFeedback')}
                    description={t('feedback.noFeedbackDesc')}
                />
            ) : (
                <CardBody className='p-0'>
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                    <th className='px-4 py-2.5'>
                                        {t('feedback.colDate')}
                                    </th>
                                    <th className='px-4 py-2.5'>
                                        {t('feedback.colCategory')}
                                    </th>
                                    <th className='px-4 py-2.5'>
                                        {t('feedback.colUser')}
                                    </th>
                                    <th className='px-4 py-2.5'>
                                        {t('feedback.colMessage')}
                                    </th>
                                    <th className='px-4 py-2.5'>
                                        {t('feedback.colPath')}
                                    </th>
                                    <th className='px-4 py-2.5 text-right'>
                                        {t('feedback.colStatus')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((fb: Feedback) => {
                                    const cat =
                                        feedbackCategoryMeta[fb.category]
                                    const st = feedbackStatusMeta[fb.status]
                                    return (
                                        <tr
                                            key={fb.id}
                                            className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                        >
                                            <td className='px-4 py-2.5 whitespace-nowrap text-slate-500 dark:text-slate-400'>
                                                {formatDateTime(fb.createdAt)}
                                            </td>
                                            <td className='px-4 py-2.5'>
                                                <Badge tone={cat.tone}>
                                                    {t(cat.labelKey)}
                                                </Badge>
                                            </td>
                                            <td className='px-4 py-2.5'>
                                                <span className='block font-medium text-slate-800 dark:text-slate-100'>
                                                    {fb.userName}
                                                </span>
                                                <span className='block text-xs text-slate-500 dark:text-slate-400'>
                                                    {fb.email}
                                                </span>
                                            </td>
                                            <td
                                                className='max-w-xs cursor-pointer truncate px-4 py-2.5 text-slate-600 underline decoration-slate-300 decoration-1 underline-offset-2 hover:decoration-slate-500 dark:text-slate-300 dark:decoration-slate-600 dark:hover:decoration-slate-400'
                                                title={t('feedback.showFull')}
                                                onClick={() => setDetail(fb)}
                                            >
                                                {fb.message}
                                            </td>
                                            <td
                                                className='max-w-[10rem] truncate px-4 py-2.5 font-mono text-xs text-slate-500 dark:text-slate-400'
                                                title={fb.path}
                                            >
                                                {fb.path}
                                            </td>
                                            <td className='px-4 py-2.5'>
                                                <div className='flex items-center justify-end gap-1.5'>
                                                    <Badge tone={st.tone}>
                                                        {t(st.labelKey)}
                                                    </Badge>
                                                    {fb.status === 'OPEN' && (
                                                        <>
                                                            <Button
                                                                variant='secondary'
                                                                size='sm'
                                                                loading={rowPending(fb.id)}
                                                                onClick={() =>
                                                                    setStatus.mutate({
                                                                        id: fb.id,
                                                                        status: 'READ',
                                                                    })
                                                                }
                                                            >
                                                                {t('feedback.markRead')}
                                                            </Button>
                                                            <Button
                                                                size='sm'
                                                                loading={rowPending(fb.id)}
                                                                onClick={() =>
                                                                    setStatus.mutate({
                                                                        id: fb.id,
                                                                        status: 'DONE',
                                                                    })
                                                                }
                                                            >
                                                                {t('feedback.markDone')}
                                                            </Button>
                                                        </>
                                                    )}
                                                    {fb.status === 'READ' && (
                                                        <Button
                                                            size='sm'
                                                            loading={rowPending(fb.id)}
                                                            onClick={() =>
                                                                setStatus.mutate({
                                                                    id: fb.id,
                                                                    status: 'DONE',
                                                                })
                                                            }
                                                        >
                                                            {t('feedback.markDone')}
                                                        </Button>
                                                    )}
                                                    {fb.status === 'DONE' && (
                                                        <Button
                                                            variant='secondary'
                                                            size='sm'
                                                            loading={rowPending(fb.id)}
                                                            onClick={() =>
                                                                setStatus.mutate({
                                                                    id: fb.id,
                                                                    status: 'OPEN',
                                                                })
                                                            }
                                                        >
                                                            {t('feedback.reopen')}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className='flex items-center justify-between border-t border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400'>
                        <span>
                            {t('feedback.entriesOf', {
                                shown: rows.length,
                                total: meta?.total ?? rows.length,
                            })}
                        </span>
                        {hasMore && (
                            <Button
                                variant='secondary'
                                size='sm'
                                loading={feedback.isFetchingNextPage}
                                onClick={() => void feedback.fetchNextPage()}
                            >
                                {t('admin.loadMore')}
                            </Button>
                        )}
                    </div>
                </CardBody>
            )}
            <Dialog
                open={detail !== null}
                onClose={() => setDetail(null)}
                title={t('feedback.detailTitle')}
                wide
            >
                {detail && <FeedbackDetailBody fb={detail} />}
            </Dialog>
        </Card>
    )
}

/** Schreibgeschützter Detail-Dialog-Inhalt — Daten aus der Liste, kein Extra-Fetch. */
function FeedbackDetailBody({ fb }: { fb: Feedback }) {
    const t = useT()
    const cat = feedbackCategoryMeta[fb.category]
    const st = feedbackStatusMeta[fb.status]
    return (
        <div className='space-y-4'>
            <div className='flex flex-wrap items-center gap-2'>
                <Badge tone={cat.tone}>{t(cat.labelKey)}</Badge>
                <Badge tone={st.tone}>{t(st.labelKey)}</Badge>
                <span className='text-xs text-slate-500 dark:text-slate-400'>
                    {formatDateTime(fb.createdAt)}
                </span>
            </div>
            <div className='text-sm'>
                <p className='font-medium text-slate-800 dark:text-slate-100'>
                    {fb.userName}
                </p>
                <p className='text-slate-500 dark:text-slate-400'>{fb.email}</p>
            </div>
            <div>
                <p className='text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                    {t('feedback.colPath')}
                </p>
                <p className='mt-0.5 font-mono text-xs break-all text-slate-500 dark:text-slate-400'>
                    {fb.path}
                </p>
            </div>
            <div className='rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3'>
                <p className='text-sm break-words whitespace-pre-wrap text-slate-700 dark:text-slate-200'>
                    {fb.message}
                </p>
            </div>
        </div>
    )
}

function UsersPanel() {
    const t = useT()
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')

    // Debounce für die serverseitige Suche
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 300)
        return () => clearTimeout(t)
    }, [searchInput])

    const users = useAdminUsers(search)
    const rows = users.data?.pages.flatMap(p => p.data) ?? []
    const meta = users.data?.pages[users.data.pages.length - 1]?.meta
    const hasMore = users.hasNextPage

    return (
        <Card>
            <CardHeader
                title={t('admin.usersTitle')}
                actions={
                    <Input
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        placeholder={t('admin.userSearch')}
                        className='w-64'
                    />
                }
            />
            {users.isLoading ? (
                <PageLoader />
            ) : rows.length === 0 ? (
                <EmptyState
                    title={t('admin.noUsers')}
                    description={t('admin.noUsersDesc')}
                />
            ) : (
                <CardBody className='p-0'>
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                    <th className='px-4 py-2.5'>{t('workspace.name')}</th>
                                    <th className='px-4 py-2.5'>{t('workspace.email')}</th>
                                    <th className='px-4 py-2.5'>{t('admin.registered')}</th>
                                    <th className='px-4 py-2.5'>{t('admin.languageCol')}</th>
                                    <th className='px-4 py-2.5 text-right'>
                                        {t('admin.workspacesCol')}
                                    </th>
                                    <th className='px-4 py-2.5 text-right'>
                                        {t('admin.kpiTracked30')}
                                    </th>
                                    <th className='px-4 py-2.5'>
                                        {t('admin.lastActive')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((u: AdminUser) => (
                                    <tr
                                        key={u.id}
                                        className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                    >
                                        <td className='px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100'>
                                            {u.name}
                                            {u.isAdmin && (
                                                <span className='ml-1.5'>
                                                    <Badge tone='violet'>
                                                        {t('admin.adminBadge')}
                                                    </Badge>
                                                </span>
                                            )}
                                            {!u.isEmailVerified && (
                                                <span className='ml-1.5'>
                                                    <Badge tone='amber'>
                                                        {t('admin.notVerified')}
                                                    </Badge>
                                                </span>
                                            )}
                                        </td>
                                        <td className='px-4 py-2.5 text-slate-500 dark:text-slate-400'>
                                            {u.email}
                                        </td>
                                        <td className='px-4 py-2.5 whitespace-nowrap text-slate-500 dark:text-slate-400'>
                                            {formatDate(u.createdAt)}
                                        </td>
                                        <td className='px-4 py-2.5 text-slate-500 dark:text-slate-400'>
                                            {t(
                                                u.userLanguage === 'de'
                                                    ? 'settings.languageDe'
                                                    : 'settings.languageEn',
                                            )}
                                        </td>
                                        <td className='px-4 py-2.5 text-right text-slate-600 dark:text-slate-300 tabular-nums'>
                                            {u.workspacesCount}
                                        </td>
                                        <td className='px-4 py-2.5 text-right text-slate-600 dark:text-slate-300 tabular-nums'>
                                            {formatDurationCompact(
                                                u.trackedSeconds30d,
                                            )}
                                        </td>
                                        <td className='px-4 py-2.5 whitespace-nowrap text-slate-500 dark:text-slate-400'>
                                            {u.lastActiveAt
                                                ? formatDateTime(u.lastActiveAt)
                                                : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className='flex items-center justify-between border-t border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400'>
                        <span>
                            {t('admin.usersOf', {
                                shown: rows.length,
                                total: meta?.total ?? rows.length,
                            })}
                        </span>
                        {hasMore && (
                            <Button
                                variant='secondary'
                                size='sm'
                                loading={users.isFetchingNextPage}
                                onClick={() => void users.fetchNextPage()}
                            >
                                {t('admin.loadMore')}
                            </Button>
                        )}
                    </div>
                </CardBody>
            )}
        </Card>
    )
}
